import { motion, useReducedMotion } from "framer-motion";

const SRC = `${process.env.PUBLIC_URL}/assets/ascend-logo.png`;
// Pixel-measured from the artwork so slices recompose the original exactly
const BAND_TOP = 67.63;
const BAND_BOTTOM = 9.14;
const LETTER_EDGES = [0, 17.87, 31.19, 48.72, 63.54, 82.24, 100];
const LETTERS = ["A", "S", "C", "E", "N", "D"];
const BASE_DELAY = 0.75;
const STAGGER = 0.13;
const ease = [0.22, 1, 0.36, 1];

const Slice = ({ clip, testId, ...motionProps }) => (
  <motion.img
    src={SRC}
    alt=""
    aria-hidden="true"
    draggable={false}
    data-testid={testId}
    className="logo-img absolute inset-0 h-full w-full"
    style={{ clipPath: clip }}
    {...motionProps}
  />
);

export const LogoReveal = () => {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <img
        src={SRC}
        alt="ASCEND Finance Group"
        className="logo-img relative h-auto w-full"
        draggable={false}
      />
    );
  }

  const shimmerStart = BASE_DELAY + STAGGER * LETTERS.length + 0.25;

  return (
    <div
      data-testid="logo-reveal"
      role="img"
      aria-label="ASCEND Finance Group"
      className="relative w-full"
      style={{ aspectRatio: "901 / 777" }}
    >
      <Slice
        testId="logo-emblem"
        clip={`inset(0 0 ${100 - BAND_TOP}% 0)`}
        initial={{ opacity: 0, y: 10, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.35, ease }}
      />

      {LETTERS.map((letter, i) => (
        <Slice
          key={letter}
          testId={`logo-letter-${letter}`}
          clip={`inset(${BAND_TOP}% ${100 - LETTER_EDGES[i + 1]}% ${BAND_BOTTOM}% ${LETTER_EDGES[i]}%)`}
          initial={{ opacity: 0, y: 8, filter: "brightness(2.6) saturate(0.4)" }}
          animate={{
            opacity: 1,
            y: 0,
            filter: [
              "brightness(2.6) saturate(0.4)",
              "brightness(1.9) saturate(0.9)",
              "brightness(1) saturate(1)",
            ],
          }}
          transition={{
            duration: 0.7,
            delay: BASE_DELAY + i * STAGGER,
            ease,
            filter: { duration: 0.9, delay: BASE_DELAY + i * STAGGER, times: [0, 0.4, 1] },
          }}
        />
      ))}

      <Slice
        testId="logo-tagline"
        clip={`inset(${100 - BAND_BOTTOM}% 0 0 0)`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: shimmerStart - 0.1, ease }}
      />

      <motion.div
        aria-hidden="true"
        data-testid="logo-shimmer"
        className="logo-shimmer pointer-events-none absolute inset-0"
        style={{
          clipPath: `inset(${BAND_TOP}% 0 ${BAND_BOTTOM}% 0)`,
          WebkitMaskImage: `url(${SRC})`,
          maskImage: `url(${SRC})`,
        }}
        initial={{ backgroundPosition: "-120% 0", opacity: 0 }}
        animate={{ backgroundPosition: "220% 0", opacity: [0, 1, 1, 0] }}
        transition={{
          duration: 1.4,
          delay: shimmerStart,
          ease: [0.4, 0, 0.2, 1],
          opacity: { duration: 1.4, delay: shimmerStart, times: [0, 0.15, 0.8, 1] },
        }}
      />
    </div>
  );
};
